import { POLLING_INTERVAL_SECONDS, type ConfigField, type TrackingAdapterSummary, type TrackingIntegration } from '@chukua/common';

export type ConfigFormValues = Record<string, string | number | boolean | undefined | null>;

/**
 * What the form holds → what the API takes. Blank optional fields are left out, so an unset optional doesn't
 * reach the vendor as an empty string. A blank secret is sent as nothing too: on an edit that means "keep
 * the saved one" (the API's merge), on an install the schema rejects it as required.
 */
export function toConfigPayload(fields: ConfigField[], values: ConfigFormValues): Record<string, string | number | boolean> {
  const payload: Record<string, string | number | boolean> = {};
  for (const field of fields) {
    const value = values[field.name];
    if (value === undefined || value === null || value === '') continue;
    payload[field.name] = value;
  }
  return payload;
}

/** The form's starting values for an edit: the saved non-secret values. Secrets are never prefilled. */
export function initialConfigValues(fields: ConfigField[], saved: Record<string, string | number | boolean>): ConfigFormValues {
  return Object.fromEntries(fields.filter((field) => !field.secret).map((field) => [field.name, saved[field.name]]));
}

/** An install starts from the adapter's name and the default interval; an edit from what is saved. */
export function integrationFormInitialValues(adapter: TrackingAdapterSummary, saved: TrackingIntegration | undefined): ConfigFormValues {
  return {
    displayName: saved?.displayName ?? adapter.displayName,
    pollingIntervalSeconds: saved?.pollingIntervalSeconds ?? POLLING_INTERVAL_SECONDS.default,
    ...initialConfigValues(adapter.fields, saved?.config ?? {}),
  };
}
